<template>
  <div>
    <NuxtLazyHydrate when-idle>
      <NuxtLoadingIndicator color="#4ab5bd" :throttle="0" />
    </NuxtLazyHydrate>
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>
<script setup lang="ts">
import { useCheckForAppUpdates } from '~/composables/useCheckForAppUpdates'
import { useAffiliateTracking } from '~/composables/useAffiliateTracking'
import { useSocialLoginTracking } from '~/composables/useSocialLoginTracking'
import { useVwoScript } from '~/composables/useVwoScript'
import { useMidaScript } from '~/composables/useMidaScript'
import { useBodyScrollLockRouteSync } from '~/composables/useBodyScrollLock'
import { useSearchEngineForceHead } from '~/composables/search/useSearchEngineForceHead'
import { reloadKftLocalStorageFromStorage } from '~/composables/useKftLocalStorage'
import useAuth from '~/composables/auth/useAuth'

const { checkForAppUpdates } = useCheckForAppUpdates()
// Reliably release the drawer body scroll lock on every navigation, even onto
// pages that don't mount the drawer components (e.g. blank-layout checkout)
useBodyScrollLockRouteSync()
// Initialize affiliate tracking
useAffiliateTracking()
// Fire the social-login GTM event on the post-login destination (KFT-3806)
useSocialLoginTracking()
// Expose window.kftSearchEngine.force before any A/B tool loads (KFT-4115)
useSearchEngineForceHead()
// Initialize VWO A/B testing (only in production, excludes embedded/widgets/booking solution)
useVwoScript()
// Initialize Mida A/B testing (only in production, excludes embedded/widgets/booking solution)
useMidaScript()

const route = useRoute()
const { isAuthenticated, checkAuthStatus } = useAuth()

// A back/forward-cache restore keeps the in-memory state the page had when it
// was left; resync persisted state (cart etc.) and the session instead of
// reloading the page, which defeated bfcache on every back/forward
// (KFT-3614, origin KFT-1327). The session is re-checked only where it can be
// stale and matter: a signed-in page (the user may have logged out since) and
// checkout (the user may have logged in since).
const handlePageShow = (evt: PageTransitionEvent) => {
  if (!evt.persisted) return
  reloadKftLocalStorageFromStorage()
  if (isAuthenticated.value || route.path.includes('/checkout')) {
    checkAuthStatus().catch((error) =>
      console.error('Session re-check after bfcache restore failed', error)
    )
  }
}

onMounted(() => {
  usePollingWhenTabActive(checkForAppUpdates, 60 * 60 * 1000)
  window.addEventListener('pageshow', handlePageShow, false)
})

onUnmounted(() => {
  window.removeEventListener('pageshow', handlePageShow, false)
})
</script>
